import React, { useCallback, useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import '@css/pages/SummitLinkSelectionModal.scss'
import {
  AddEventsToCategoryDocument,
  CacheCommunityFragment,
  CacheCommunityFragmentDoc,
  CommunityType,
  GetCommunityLivestreamEventsDocument,
  GetContentSortOrderDocument,
  PageType,
} from '~/api/generated/graphql'
import { useWindowSize } from '~/common/hooks/useWindowSize'
import PageCommunityTypeahead from '~/pages/page/PageCommunityTypeahead'
import { getFullName, getStartOfCurrentDay, searchCache } from '~/utils'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { SummitTitleErrorType, useSummitCategoryPosts } from '~/contexts/SummitCategoryPostsContext'

type EventSelectionModalProps = {
  showModal: boolean
  setShowModal: (b: boolean) => void
  category: string
  categoryId: string
  setPendingSummitPostIds?: React.Dispatch<React.SetStateAction<string[]>>
  isVeevaOnly: boolean
}

export type CommunityToDisplay = {
  name: string
  communityId: string
}

type EventsDisplayList = {
  eventId: string
  author: string
  title: string
  draft: boolean
  checked: boolean
  selected: boolean
  inPast: boolean
}

type EventSelectionListProps = {
  events: EventsDisplayList[]
  categoryId: string
  setShowModal: (b: boolean) => void
  setCommunityId: (s: string) => void
  setPendingSummitPostIds?: React.Dispatch<React.SetStateAction<string[]>>
}

const EventSelectionList = ({
  events,
  categoryId,
  setShowModal,
  setCommunityId,
  setPendingSummitPostIds,
}: EventSelectionListProps) => {
  const [checkedEvents, setCheckedEvents] = useState<Map<string, boolean>>(new Map<string, boolean>())
  const [addEventsToCategory] = useMutation(AddEventsToCategoryDocument)
  const { pageType, setEditModePostIds, setAllPendingPostIds } = useSummitCategoryPosts()
  const { isCondensed } = useWindowSize()

  const checkBox = useCallback(
    (i: string) => {
      setCheckedEvents(events => {
        const newCheckedEvents = new Map<string, boolean>()
        // es lint mistakes the following line for an array
        // eslint-disable-next-line unicorn/no-array-for-each
        events.forEach((v, k) => {
          newCheckedEvents.set(k, v)
        })
        const newValue = !(newCheckedEvents.has(i) && newCheckedEvents.get(i))
        newCheckedEvents.set(i, newValue)
        return newCheckedEvents
      })
    },
    [setCheckedEvents]
  )

  const cancelEventSelection = useCallback(() => {
    setShowModal(false)
    setCommunityId('')
  }, [setShowModal, setCommunityId])

  const saveEventSelection = useCallback(() => {
    const postIds: string[] = []
    const ids: string[] = []

    for (const element of events) {
      if (checkedEvents.get(element.eventId)) {
        postIds.push(`{evt}${element.eventId}`)
        ids.push(element.eventId)
      }
    }

    if (pageType === PageType.Summit) {
      setEditModePostIds?.(editingIds => {
        const pendingIds = new Map<string, SummitTitleErrorType>(editingIds)
        for (const id of postIds) {
          pendingIds.set(id, SummitTitleErrorType.NEW_UNSAVED)
        }

        return pendingIds
      })
      setPendingSummitPostIds?.(pendingIds => {
        return (pendingIds ?? []).concat(postIds)
      })
      setAllPendingPostIds?.(pendingIds => {
        return new Set([...(pendingIds ?? []), ...postIds])
      })
    } else if (ids.length > 0) {
      void addEventsToCategory({
        variables: { categoryId: categoryId, eventIds: ids },
      }).then(resp => {
        if (!resp.data?.addEventsToCategory?.ok) {
          return
        }
      })
    }

    setShowModal(false)
    setCommunityId('')
  }, [
    checkedEvents,
    addEventsToCategory,
    categoryId,
    setAllPendingPostIds,
    setEditModePostIds,
    setPendingSummitPostIds,
    events,
    setShowModal,
    setCommunityId,
    pageType,
  ])

  return (
    <>
      <Modal.Body>
        <div>
          <div className={'content-table'}>
            <div className={'content-labels'}>
              <p>Author</p>
              <p>Title</p>
            </div>
            <div className={'content-rows'}>
              {events &&
                events.map(event => (
                  <div
                    className={`content-row ${(event.selected || event.inPast) && 'selected-event'} ${isCondensed ? 'condensed' : ''}`}
                    key={event.eventId}
                    data-testid={'content-row'}
                  >
                    <div className={'checkbox-container'}>
                      <input
                        type={'checkbox'}
                        defaultChecked={checkedEvents.has(event.eventId) && checkedEvents.get(event.eventId)}
                        onChange={() => checkBox(event.eventId)}
                        disabled={event.selected || event.inPast}
                        data-testid={'checkbox'}
                      />
                    </div>
                    <p>{event.author}</p>
                    <div className={'content-title-container'}>
                      {event.draft && <div className={'draft-indicator'}>DRAFT</div>}
                      <p>{event.title}</p>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" size="sm" onClick={cancelEventSelection}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={saveEventSelection} data-testid={'add-events-modal'}>
          Add Events
        </Button>
      </Modal.Footer>
    </>
  )
}

const EventSelectionModal = ({
  showModal,
  setShowModal,
  category,
  categoryId,
  setPendingSummitPostIds,
  isVeevaOnly,
}: EventSelectionModalProps) => {
  const { isCondensed } = useWindowSize()
  const [communityId, setCommunityId] = useState('')
  const { allPendingPostIds } = useSummitCategoryPosts()

  const { data: eventsData } = useQuery(GetCommunityLivestreamEventsDocument, {
    variables: { communityId: communityId, date: getStartOfCurrentDay() },
    skip: !showModal || !communityId,
  })
  const { data: sortOrderData } = useQuery(GetContentSortOrderDocument, {
    variables: { id: communityId },
    skip: !showModal || !communityId,
    fetchPolicy: 'cache-and-network',
  })

  const [communitiesOptions, setCommunitiesOptions] = useState<CommunityToDisplay[]>([])

  const eventsToDisplay = useMemo(() => {
    if (!eventsData) {
      return []
    }

    const events = new Map()

    eventsData?.community?.events?.edges
      ?.filter(event => !event?.node?.hidden)
      .map(event => {
        events.set(event?.node?.eventId, {
          eventId: event?.node?.eventId ?? '',
          draft: event?.node?.draft ?? false,
          author: getFullName(event?.node?.createdBy),
          inPast: event?.node?.inPast,
          title: event?.node?.title ?? '',
          checked: false,
          selected: !!event?.node?.categoryId || allPendingPostIds?.has(event?.node?.eventId ?? ''),
        } as EventsDisplayList)
      })

    const orderedEvents: EventsDisplayList[] = []
    const unorderedEvents: EventsDisplayList[] = []

    // Organize event list into content order
    const orderedIds = (JSON.parse(sortOrderData?.community?.contentOrder ?? null) as string[]) ?? []

    // add events included in the content order
    for (const orderedId of orderedIds) {
      const event = events.get(orderedId)
      if (event) orderedEvents.push(event)
    }

    // add remaining events that aren't in the content order and append to the end of the list
    for (const [eventId] of events.entries()) {
      if (!orderedIds.includes(eventId)) {
        const event = events.get(eventId)
        if (event) unorderedEvents.push(event)
      }
    }
    return orderedEvents.concat(unorderedEvents)
  }, [allPendingPostIds, eventsData, sortOrderData])

  const { cache } = useApolloClient()

  const handleSearch = (search: string) => {
    const normalizedSearch = search.normalize('NFKD')
    const filteredResults = searchCache(normalizedSearch)
    const communityType = isVeevaOnly ? CommunityType.Veevan : CommunityType.Public

    const newOptions = filteredResults.map(k => {
      const [objectType, id] = k.split(':')
      if (objectType === 'Community') {
        const community = cache.readFragment<CacheCommunityFragment>({
          id: cache.identify({ __typename: 'Community' as const, communityId: id }),
          fragment: CacheCommunityFragmentDoc,
        })

        if (community?.type == communityType) {
          return {
            name: community?.name ?? '',
            communityId: community?.communityId ?? '',
          }
        }
      }

      return { name: '', communityId: '' }
    })

    setCommunitiesOptions(newOptions)
  }

  return (
    <Modal
      show={showModal}
      onHide={() => {
        setShowModal(false)
        setCommunityId('')
      }}
      className={`event-selection-modal ${isCondensed && 'condensed'}`}
    >
      <Modal.Header>
        <div>
          <h4>Add Events</h4>
          <table className={'modal-options'}>
            <tbody>
              <tr className={'modal-option-row'}>
                <td>Category</td>
                <td>{category}</td>
              </tr>
              <tr className={`modal-option-row ${isCondensed ? 'condensed' : ''}`}>
                <td>Community</td>
                <td>
                  <PageCommunityTypeahead
                    options={communitiesOptions}
                    handleSearch={handleSearch}
                    onSelect={(s: string) => setCommunityId(s)}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal.Header>
      {eventsToDisplay.length > 0 && communityId ? (
        <EventSelectionList
          events={eventsToDisplay}
          categoryId={categoryId}
          setShowModal={setShowModal}
          setCommunityId={setCommunityId}
          setPendingSummitPostIds={setPendingSummitPostIds}
        />
      ) : (
        <>
          <Modal.Body>
            <div>
              <div className={'content-table'}>
                <div className={'content-labels'}>
                  <p>Author</p>
                  <p>Title</p>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light"
              size="sm"
              onClick={() => {
                setShowModal(false)
                setCommunityId('')
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setShowModal(false)
                setCommunityId('')
              }}
              data-testid={'add-events-modal'}
            >
              Add Events
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  )
}

export default EventSelectionModal
